import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import PhonelinkSetupIcon from "@mui/icons-material/PhonelinkSetup";
import PieChartIcon from "@mui/icons-material/PieChart";
import SecurityUpdateGoodIcon from "@mui/icons-material/SecurityUpdateGood";
import SettingsIcon from "@mui/icons-material/Settings";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CloudIcon from "@mui/icons-material/Cloud";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { useMediaQuery } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import { useTheme } from "mui-styles";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/hooks/use-auth";
import HomeSmileIcon from "src/icons/untitled-ui/duocolor/home-smile";
import LineChartUp04Icon from "src/icons/untitled-ui/duocolor/line-chart-up-04";
import Users03Icon from "src/icons/untitled-ui/duocolor/users-03";
import { paths } from "src/paths";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  roles?: string[];
}

export interface Section {
  items: Item[];
  subheader?: string;
}

const excludedItems: string[] = [];

export const useSections = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();

  const sections = useMemo<Section[]>(() => {
    return [
      {
        items: [
          {
            title: "Home",
            path: paths.dashboard.index,
            icon: (
              <SvgIcon fontSize="small">
                <HomeSmileIcon />
              </SvgIcon>
            ),
            roles: ["nightscout"],
          },
          {
            title: "Analytics",
            path: paths.dashboard.optimization.index,
            icon: (
              <SvgIcon fontSize="small">
                <LineChartUp04Icon />
              </SvgIcon>
            ),
            roles: ["nightscout"],
            items: [
              {
                title: "Profiles",
                path: paths.dashboard.profiles,
              },
              {
                title: "Column Chart",
                path: paths.dashboard.columnchart,
              },
              {
                title: "Heatmap",
                path: paths.dashboard.heatmapchart,
              },
              {
                title: "Pie Chart",
                path: paths.dashboard.piechart,
              },
              {
                title: "Time Series",
                path: paths.dashboard.timeseries,
              },
              {
                title: "Percentile",
                path: paths.dashboard.percentilechart,
              },
              {
                title: "Moving Average",
                path: paths.dashboard.glucosemovingaverage,
              },
            ],
          },
          {
            title: "Resources",
            icon: (
              <SvgIcon fontSize="small">
                <MenuBookIcon />
              </SvgIcon>
            ),
            items: [
              {
                title: "Apps",
                path: paths.dashboard.software.index,
                roles: ["nightscout"],
              },

              {
                title: "Basal Testing Calculator",
                path: paths.dashboard.basalTestingCalculator,
              },
            ],
          },
          {
            title: "Cloud Builder",
            path: paths.dashboard.apkbuild,
            icon: (
              <SvgIcon fontSize="small">
                <CloudIcon />
              </SvgIcon>
            ),
            roles: ["admin", "nightscout"],
            items: [
              {
                title: "AndroidAPS",
                path: paths.dashboard.apkbuild,
              },
              {
                title: "Trio",
                path: paths.dashboard.apkbuildTrio,
              },
              {
                title: "iLoop",
                path: paths.dashboard.apkbuildILoop,
              },
              {
                title: "Files",
                path: paths.dashboard.apkbuildFiles,
              },
            ],
          },
          {
            title: "Admin",
            path: paths.dashboard.customers.index,
            icon: (
              <SvgIcon fontSize="small">
                <Users03Icon />
              </SvgIcon>
            ),
            roles: ["admin"],
            items: [
              {
                title: "Loopers",
                path: paths.dashboard.customers.index,
              },
              {
                title: "Finance",
                path: paths.dashboard.finance,
              },
            ],
          },
          {
            title: "Nightscout",
            path: paths.dashboard.settings,
            icon: (
              <SvgIcon fontSize="small">
                <SettingsIcon />
              </SvgIcon>
            ),
            roles: ["nightscout"],
            items: [
              {
                title: "Settings",
                path: paths.dashboard.settings,
              },
              {
                title: "Messaging",
                path: paths.dashboard.messaging,
                roles: ["mcp"],
              },
              {
                title: "Transfer Data",
                path: paths.dashboard.nightscoutTransfer,
              },
              // {
              //   title: "Delete Data",
              //   path: paths.dashboard.deleteNightscoutData,
              // },
              {
                title: "LibreLink Up",
                path: paths.dashboard.cloudServices.libreLink,
              },
              {
                title: "AI Chat",
                path: paths.dashboard.cloudServices.mcpNightscout,
                roles: ["mcp"],
              },
            ],
          },
        ],
      },

      // COMMENTED OUT ITEMS - MOVED TO END
      // {
      //   title: "AI assistant",
      //   path: paths.dashboard.aiassistant,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <AssistantIcon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   title: "Testing",
      //   path: paths.dashboard.optimization.index,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <GraduationHat01Icon />
      //     </SvgIcon>
      //   ),
      //   items: [
      //     {
      //       title: "Autotune",
      //       path: paths.dashboard.optimization.courseDetails1,
      //     },
      //     {
      //       title: "Notes analysis",
      //       path: paths.dashboard.reminders.index,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <LayoutAlt02Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: "Profiles overview",
      //       path: paths.dashboard.profiles,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <LineChartUp04Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: "Reminders",
      //       path: paths.dashboard.optimization.courseDetails2,
      //       items: [
      //         {
      //           title: "Basals",
      //           path: paths.dashboard.reminders.basals,
      //         },
      //         {
      //           title: "ISF",
      //           path: paths.dashboard.reminders.isf,
      //         },
      //         {
      //           title: "CR",
      //           path: paths.dashboard.reminders.cr,
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   title: "Learning Hub",
      //   path: paths.dashboard.academy.index,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <GraduationHat01Icon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   title: "Monitoring",
      //   path: paths.dashboard.monitoring,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <LineChartUp04Icon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   title: "Updates",
      //   path: paths.dashboard.academy.courseDetails,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <PhonelinkSetupIcon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   subheader: "Optimization",
      //   items: [],
      // },
      // {
      //   subheader: "LoopHub",
      //   items: [
      //     {
      //       items: [
      //         {
      //           title: "Users",
      //           path: paths.dashboard.customers.index,
      //         },
      //         {
      //           title: t(tokens.nav.profile),
      //           path: paths.dashboard.social.profile,
      //         },
      //         {
      //           title: t(tokens.nav.feed),
      //           path: paths.dashboard.social.feed,
      //         },
      //       ],
      //     },
      //     {
      //       title: t(tokens.nav.analytics),
      //       path: paths.dashboard.analytics,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <BarChartSquare02Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //   ],
      // },
      // {
      //   title: t(tokens.nav.blog),
      //   path: paths.dashboard.blog.index,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <LayoutAlt02Icon />
      //     </SvgIcon>
      //   ),
      //   items: [
      //     {
      //       title: t(tokens.nav.postList),
      //       path: paths.dashboard.blog.index,
      //     },
      //     {
      //       title: t(tokens.nav.postDetails),
      //       path: paths.dashboard.blog.postDetails,
      //     },
      //     {
      //       title: t(tokens.nav.postCreate),
      //       path: paths.dashboard.blog.postCreate,
      //     },
      //   ],
      // },
      // {
      //   title: t(tokens.nav.chat),
      //   path: paths.dashboard.chat,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <MessageChatSquareIcon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   title: "Events",
      //   path: paths.dashboard.calendar,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <CalendarIcon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   subheader: "Admin",
      //   items: [
      //     {
      //       title: "Members",
      //       path: paths.dashboard.customers.index,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <Users03Icon />
      //         </SvgIcon>
      //       ),
      //       items: [
      //         {
      //           title: t(tokens.nav.list),
      //           path: paths.dashboard.customers.index,
      //         },
      //         {
      //           title: t(tokens.nav.details),
      //           path: paths.dashboard.customers.details,
      //         },
      //         {
      //           title: t(tokens.nav.edit),
      //           path: paths.dashboard.customers.edit,
      //         },
      //       ],
      //     },
      //     {
      //       title: t(tokens.nav.kanban),
      //       path: paths.dashboard.kanban,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <CheckDone01Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: t(tokens.nav.fileManager),
      //       path: paths.dashboard.fileManager,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <Upload04Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //   ],
      // },
      // {
      //   subheader: t(tokens.nav.pages),
      //   items: [
      //     {
      //       title: t(tokens.nav.auth),
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <Lock01Icon />
      //         </SvgIcon>
      //       ),
      //       items: [
      //         {
      //           title: t(tokens.nav.login),
      //           items: [
      //             {
      //               title: "Classic",
      //               path: paths.authDemo.login.classic,
      //             },
      //             {
      //               title: "Modern",
      //               path: paths.authDemo.login.modern,
      //             },
      //           ],
      //         },
      //         {
      //           title: t(tokens.nav.register),
      //           items: [
      //             {
      //               title: "Classic",
      //               path: paths.authDemo.register.classic,
      //             },
      //             {
      //               title: "Modern",
      //               path: paths.authDemo.register.modern,
      //             },
      //           ],
      //         },
      //         {
      //           title: t(tokens.nav.forgotPassword),
      //           items: [
      //             {
      //               title: "Classic",
      //               path: paths.authDemo.forgotPassword.classic,
      //             },
      //             {
      //               title: "Modern",
      //               path: paths.authDemo.forgotPassword.modern,
      //             },
      //           ],
      //         },
      //         {
      //           title: t(tokens.nav.resetPassword),
      //           items: [
      //             {
      //               title: "Classic",
      //               path: paths.authDemo.resetPassword.classic,
      //             },
      //             {
      //               title: "Modern",
      //               path: paths.authDemo.resetPassword.modern,
      //             },
      //           ],
      //         },
      //         {
      //           title: t(tokens.nav.verifyCode),
      //           items: [
      //             {
      //               title: "Classic",
      //               path: paths.authDemo.verifyCode.classic,
      //             },
      //             {
      //               title: "Modern",
      //               path: paths.authDemo.verifyCode.modern,
      //             },
      //           ],
      //         },
      //       ],
      //     },
      //     {
      //       title: t(tokens.nav.pricing),
      //       path: paths.pricing,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <CreditCard01Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: t(tokens.nav.checkout),
      //       path: paths.checkout,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <LogOut01Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: t(tokens.nav.contact),
      //       path: paths.workshop,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <Mail04Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: t(tokens.nav.error),
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <XSquareIcon />
      //         </SvgIcon>
      //       ),
      //       items: [
      //         {
      //           title: "401",
      //           path: paths.notAuthorized,
      //         },
      //         {
      //           title: "404",
      //           path: paths.notFound,
      //         },
      //         {
      //           title: "500",
      //           path: paths.serverError,
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   subheader: "Misc",
      //   items: [
      //     {
      //       title: "Level 0",
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <AlignLeft02Icon />
      //         </SvgIcon>
      //       ),
      //       items: [
      //         {
      //           title: "Level 1a",
      //           items: [
      //             {
      //               title: "Level 2a",
      //               items: [
      //                 {
      //                   title: "Level 3a",
      //                 },
      //                 {
      //                   title: "Level 3b",
      //                   disabled: true,
      //                 },
      //               ],
      //             },
      //             {
      //               title: "Level 2b",
      //             },
      //           ],
      //         },
      //         {
      //           title: "Level 1b",
      //         },
      //       ],
      //     },
      //     {
      //       title: "Disabled",
      //       disabled: true,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <XSquareIcon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: "Label",
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <File01Icon />
      //         </SvgIcon>
      //       ),
      //       label: <Chip color="primary" label="New" size="small" />,
      //     },
      //     {
      //       title: "Blank",
      //       path: paths.dashboard.blank,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <File01Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: "External Link",
      //       path: "https://devias.io",
      //       external: true,
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <File01Icon />
      //         </SvgIcon>
      //       ),
      //     },
      //   ],
      // },
    ];
  }, [t, mdUp, user]);

  const userHasItemPermission = (item: Item): boolean => {
    if (!item.roles || item.roles.length === 0) {
      return true;
    }
    return item.roles.some((role) => user?.roles?.includes(role));
  };

  const filterItemsByRole = (items: Item[]): Item[] =>
    items
      .filter((item) => userHasItemPermission(item))
      .map((item) =>
        item.items ? { ...item, items: filterItemsByRole(item.items) } : item
      );

  const sectionsMobileResponsive = sections.map((section) => {
    return {
      items: (section.items as Item[]).filter((item) => {
        // if no roles defined for the item give the user standard permission
        if (mdUp) return true;
        return !excludedItems.includes(item?.title);
      }),
      subheader: section.subheader,
    } as Section;
  });
  const filteredSections = sectionsMobileResponsive.map((section) => {
    return {
      items: filterItemsByRole(section.items as Item[]),
      subheader: section.subheader,
    } as Section;
  });
  return filteredSections;
};
